import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { ENGAGE_GEN_AI_BODY_MODAL_REFINED_WITH_PROMPT, ENGAGE_GEN_AI_SUBJECT_MODAL_REFINED_WITH_PROMPT, ENGAGE_GEN_AI_TITLE_MODAL_REFINED_WITH_PROMPT, RudderstackTracker, } from 'frontend/js/modules/infra/observability/rudderstack';
import { useApiMutation } from '../../../../../../infra/api';
import { useRouteParams } from '../../../../../../infra/routing';
import { endpoints } from '../../../../network';
import { useEditableField } from '../../../hooks/use-editable-field';
import { EmailContent } from './email-content';
export var EmailContentContainer = function () {
    var _a, _b, _c;
    var campaignId = useRouteParams().campaignId;
    var _d = useState([]), subjectSuggestions = _d[0], setSubjectSuggestions = _d[1];
    var _e = useState([]), titleSuggestions = _e[0], setTitleSuggestions = _e[1];
    var _f = useState([]), bodySuggestions = _f[0], setBodySuggestions = _f[1];
    // AI popover coordination state
    // Note: The actual coordination logic is handled in the EmailContent component
    // This container just provides the callback for external coordination if needed
    // Handle AI popover opening coordination
    var handleAiPopoverOpen = function (source) {
        // Could be used for analytics or external coordination in the future
        console.log("AI popover opened for: ".concat(source));
    };
    // Determine if we should force close all popovers (reset coordination)
    var forceCloseAiPopovers = false;
    // Helper function to check if rich text content is empty
    var isEmptyRichTextContent = function (content) {
        if (!content)
            return true;
        var trimmed = content.trim();
        return trimmed === '' || trimmed === '<p></p>' || /^<p[^>]*><\/p>$/.test(trimmed);
    };
    // Helper function to extract plain text from HTML content and trim it
    var getPlainTextContent = function (content) {
        if (!content)
            return '';
        // Create a temporary div to parse HTML and get text content
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        return tempDiv.textContent || tempDiv.innerText || '';
    };
    var _g = useEditableField('emailSubject'), subjectField = _g.field, setSubjectValue = _g.setValue;
    var _h = useEditableField('emailTitle'), titleField = _h.field, setTitleValue = _h.setValue;
    var _j = useEditableField('emailBody'), bodyField = _j.field, setBodyValue = _j.setValue;
    var campaignTypeField = useEditableField('type').field;
    var emailSubjectLimit = useEditableField('emailSubjectLimit').field;
    var emailBodyLimit = useEditableField('emailBodyLimit').field;
    var emailTitleLimit = useEditableField('emailTitleLimit').field;
    // Refine campaign text mutation
    var refineCampaignTextMutation = useApiMutation({
        endpoint: endpoints.refineCampaignTextMutation,
        options: {
            onError: function (error) {
                console.error('AI text refinement failed:', error);
            },
        },
    });
    var generateCampaignTextMutation = useApiMutation({
        endpoint: endpoints.generateCampaignTextMutation,
        options: {
            onError: function (error) {
                console.error('AI text generation failed:', error);
            },
        },
    });
    // Campaign suggestions mutation
    var campaignSuggestionsMutation = useApiMutation({
        endpoint: endpoints.campaignSuggestionsMutation,
        options: {
            onSuccess: function (response, variables) {
                // Convert API response to format expected by AI popover
                var formattedSuggestions = [];
                // Handle the new response format based on suggestion kind
                var section = variables.data.kind;
                if ('titles' in response) {
                    // For titles: label is the title, prompt is the same (to be inserted directly)
                    formattedSuggestions = response.titles.map(function (title) { return ({
                        label: title,
                        prompt: title, // Direct content insertion, not a refine prompt
                    }); });
                }
                else if ('subjects' in response) {
                    // For subjects: label is the subject, prompt is the same (to be inserted directly)
                    formattedSuggestions = response.subjects.map(function (subject) { return ({
                        label: subject,
                        prompt: subject, // Direct content insertion, not a refine prompt
                    }); });
                }
                else if ('body_topics' in response) {
                    // For body topics: use display_text as label, prompt for content generation
                    formattedSuggestions = response.body_topics.map(function (topic) { return ({
                        label: topic.displayText,
                        prompt: topic.prompt, // This will be used for generate API, not refine
                    }); });
                }
                else if ('images' in response) {
                    // For images: not relevant for text editors
                    formattedSuggestions = response.images.map(function (image) { return ({
                        label: image.displayText,
                        prompt: image.prompt,
                    }); });
                }
                // Update the appropriate suggestions state based on the section
                if (section === 'subject') {
                    setSubjectSuggestions(formattedSuggestions);
                }
                else if (section === 'title') {
                    setTitleSuggestions(formattedSuggestions);
                }
                else {
                    setBodySuggestions(formattedSuggestions);
                }
            },
            onError: function (error) {
                console.error('Failed to fetch campaign suggestions:', error);
            },
        },
    });
    // Function to fetch suggestions for a specific section
    var fetchSuggestions = function (section) {
        if (!campaignId) {
            console.warn('Campaign ID not available for fetching suggestions');
            return;
        }
        // Check if the current field is empty
        var currentFieldValue = '';
        if (section === 'subject') {
            currentFieldValue = subjectField.value || '';
        }
        else if (section === 'title') {
            currentFieldValue = titleField.value || '';
        }
        else if (section === 'body') {
            currentFieldValue = bodyField.value || '';
        }
        // Only fetch from API if the field is empty
        if (isEmptyRichTextContent(currentFieldValue)) {
            var kind = 'body_topic';
            if (section === 'subject') {
                kind = 'subject';
            }
            else if (section === 'title') {
                kind = 'title';
            }
            // Pass current field values as extraContext, excluding the field being updated and empty values
            var extraContext = {};
            // Only include non-empty values and exclude the field being updated
            if (section !== 'subject' && subjectField.value) {
                var plainText = getPlainTextContent(subjectField.value).trim();
                if (plainText) {
                    extraContext.subject = plainText;
                }
            }
            if (section !== 'title' && titleField.value) {
                var plainText = getPlainTextContent(titleField.value).trim();
                if (plainText) {
                    extraContext.title = plainText;
                }
            }
            if (section !== 'body' && bodyField.value) {
                var plainText = getPlainTextContent(bodyField.value).trim();
                if (plainText) {
                    extraContext.body = plainText;
                }
            }
            campaignSuggestionsMutation.mutate({
                campaignId: campaignId,
                data: {
                    kind: kind,
                    extraContext: extraContext,
                },
            });
        }
        // If field has content, we don't fetch suggestions (will use hardcoded ones)
    };
    // Function to handle AI text refinement
    var handleRefineText = function (section, prompt, originalText, partialText) {
        if (!campaignId) {
            console.warn('Campaign ID not available for AI text refinement');
            return Promise.reject(new Error('Campaign ID not available'));
        }
        // Pass current field values as extraContext, excluding the field being updated and empty values
        var extraContext = {};
        // Only include non-empty values and exclude the field being updated
        if (section !== 'subject' && subjectField.value) {
            var plainText = getPlainTextContent(subjectField.value).trim();
            if (plainText) {
                extraContext.subject = plainText;
            }
        }
        if (section !== 'title' && titleField.value) {
            var plainText = getPlainTextContent(titleField.value).trim();
            if (plainText) {
                extraContext.title = plainText;
            }
        }
        if (section !== 'body' && bodyField.value) {
            var plainText = getPlainTextContent(bodyField.value).trim();
            if (plainText) {
                extraContext.body = plainText;
            }
        }
        if (section === 'subject') {
            RudderstackTracker.getInstance().track(ENGAGE_GEN_AI_SUBJECT_MODAL_REFINED_WITH_PROMPT, {
                prompt: prompt,
                originalText: originalText,
                partialText: partialText,
            });
        }
        else if (section === 'title') {
            RudderstackTracker.getInstance().track(ENGAGE_GEN_AI_TITLE_MODAL_REFINED_WITH_PROMPT, {
                prompt: prompt,
                originalText: originalText,
                partialText: partialText,
            });
        }
        else if (section === 'body') {
            RudderstackTracker.getInstance().track(ENGAGE_GEN_AI_BODY_MODAL_REFINED_WITH_PROMPT, {
                prompt: prompt,
                originalText: originalText,
                partialText: partialText,
            });
        }
        if (originalText) {
            return refineCampaignTextMutation.mutateAsync({
                campaignId: campaignId,
                data: {
                    section: section,
                    prompt: prompt,
                    original: originalText,
                    partial: partialText,
                    extraContext: extraContext,
                },
            });
        }
        return generateCampaignTextMutation.mutateAsync({
            campaignId: campaignId,
            data: {
                section: section,
                prompt: prompt,
                extraContext: extraContext,
            },
        });
    };
    var isPromoCampaign = (campaignTypeField === null || campaignTypeField === void 0 ? void 0 : campaignTypeField.value) === 'promotion';
    // Determine which suggestions to use based on field content
    var getEffectiveSuggestions = function (section, apiSuggestions) {
        var currentFieldValue = '';
        if (section === 'subject') {
            currentFieldValue = subjectField.value || '';
        }
        else if (section === 'title') {
            currentFieldValue = titleField.value || '';
        }
        else if (section === 'body') {
            currentFieldValue = bodyField.value || '';
        }
        // If field is empty, use API suggestions; if has content, use empty array (will fall back to hardcoded)
        return isEmptyRichTextContent(currentFieldValue) ? apiSuggestions : [];
    };
    // Create wrapper functions for onRefineText that match the signature expected by RichTextEditor
    var createRefineTextHandler = function (section) {
        return function (prompt, originalText, partialText) {
            return handleRefineText(section, prompt, originalText, partialText);
        };
    };
    var subjectRefineHandler = createRefineTextHandler('subject');
    var titleRefineHandler = createRefineTextHandler('title');
    var bodyRefineHandler = createRefineTextHandler('body');
    return (_jsx(EmailContent, { subject: subjectField.value, onSubjectChange: setSubjectValue, subjectError: (_a = subjectField.error) === null || _a === void 0 ? void 0 : _a.message, title: titleField.value, onTitleChange: setTitleValue, titleError: (_b = titleField.error) === null || _b === void 0 ? void 0 : _b.message, body: bodyField.value, onBodyChange: setBodyValue, bodyError: (_c = bodyField.error) === null || _c === void 0 ? void 0 : _c.message, displayPromoInput: isPromoCampaign, emailSubjectLimit: emailSubjectLimit.value, emailBodyLimit: emailBodyLimit.value, emailTitleLimit: emailTitleLimit.value, 
        // AI functionality props - pass effective suggestions based on field content
        subjectSuggestions: getEffectiveSuggestions('subject', subjectSuggestions), titleSuggestions: getEffectiveSuggestions('title', titleSuggestions), bodySuggestions: getEffectiveSuggestions('body', bodySuggestions), onFetchSuggestions: fetchSuggestions, onRefineSubjectText: subjectRefineHandler, onRefineTitleText: titleRefineHandler, onRefineBodyText: bodyRefineHandler, isRefining: refineCampaignTextMutation.isLoading || generateCampaignTextMutation.isLoading, isFetchingSuggestions: campaignSuggestionsMutation.isLoading, 
        // Pass flags indicating if suggestions are direct content (true for empty fields, false for fields with content)
        // Note: Body suggestions are always prompts for AI refinement, never direct content
        subjectSuggestionsAreDirectContent: isEmptyRichTextContent(subjectField.value), titleSuggestionsAreDirectContent: isEmptyRichTextContent(titleField.value), bodySuggestionsAreDirectContent: false, 
        // AI popover coordination props
        onAiPopoverOpen: handleAiPopoverOpen, forceCloseAiPopovers: forceCloseAiPopovers }));
};
